// src/pages/CircuitDesign.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "../components/CircuitDesign.css";

const pub = process.env.PUBLIC_URL;
const fromPublic = (name) => `/${encodeURIComponent(name)}`;

export default function CircuitDesign() {
  useEffect(() => {
    AOS.init({ duration: 700, once: false, delay: 50 });
  }, []);

  /* ---------- data arrays (unchanged) ---------- */
  const processSteps = [
    {
      number: 1,
      title: "Initial Consultation",
      text:
        "We begin with a detailed discussion of your project requirements, goals and constraints.",
    },
    {
      number: 2,
      title: "Site Assessment",
      text:
        "Our engineers inspect your property to evaluate existing systems and identify improvements.",
    },
    {
      number: 3,
      title: "Design Development",
      text:
        "We create detailed circuit diagrams, load calculations and system specs tailored to you.",
    },
    {
      number: 4,
      title: "Client Review",
      text:
        "We present the proposed design, explain technical details and incorporate your feedback.",
    },
    {
      number: 5,
      title: "Implementation Planning",
      text:
        "Develop installation plans, material specs and timelines for seamless execution.",
    },
    {
      number: 6,
      title: "Quality Assurance",
      text:
        "Rigorous testing to ensure compliance with SANS 10142-1 and other standards.",
    },
  ];

  const galleryItems = [
    {
      src: "design.png",
      alt: "Residential Circuit Design",
      label: "Residential Systems",
    },
    {
      src: "design.png",
      alt: "Industrial Control Systems",
      label: "Industrial Controls",
    },
    {
      src: "design.png",
      alt: "Smart Home Automation",
      label: "Smart Home Integration",
    },
  ];

  const benefits = [
    {
      icon: "fas fa-shield-alt",
      title: "Safety-First Approach",
      text:
        "All designs comply with the latest safety regulations and include built-in protection.",
    },
    {
      icon: "fas fa-leaf",
      title: "Energy Efficient",
      text: "Optimised layouts that reduce waste and lower operating costs.",
    },
    {
      icon: "fas fa-expand-arrows-alt",
      title: "Future-Proof Design",
      text:
        "Scalable solutions that accommodate future expansions and tech upgrades.",
    },
  ];

  const faqs = [
    {
      q: "What standards do you follow?",
      a:
        "All designs comply with SANS 10142-1 wiring code, IEC standards and local bylaws.",
    },
    {
      q: "How long does the design process take?",
      a:
        "Residential projects 3–5 working days; commercial jobs 1–2 weeks depending on complexity.",
    },
    {
      q: "Do you provide as-built documentation?",
      a:
        "Yes — we deliver full packages including diagrams, load calcs and equipment specs.",
    },
  ];

  /* ---------- JSX ---------- */
  return (
    <>
      {/* HERO */}
      <section
        className="hero-section"
        data-aos="fade-up"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7),rgba(0,0,0,0.7)), url(${pub}/designback.png)`,
        }}
      >
        <div className="hero-content">
          <h1>Professional Circuit Design</h1>
          <p>
            Custom electrical circuit solutions for optimal performance, safety
            and efficiency.
          </p>
          <div className="hero-buttons">
            <a href="#service-process" className="btn">
              Our Process
            </a>
            <Link to="/contact" className="btn btn-outline">
              Request Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="service-intro" data-aos="fade-up">
        <div className="service-intro-content">
          <div className="service-intro-image" data-aos="fade-right">
            <img
              src={`${pub}/design.png`}
              alt="Circuit Design Services"
            />
          </div>

          <div className="service-intro-text" data-aos="fade-left">
            <h2>Expert Circuit Design Services</h2>
            <p>
              Mosike Electrical Contractors specialises in custom circuit
              solutions for residential, commercial and industrial
              applications. Our licensed engineers use the latest software and
              best practice to deliver safe, efficient and reliable systems.
            </p>
            <p>
              From simple home upgrades to complex industrial controls, we
              provide results that exceed expectations.
            </p>

            <ul className="service-features">
              <li>
                <i className="fas fa-check-circle" /> Custom circuit design
              </li>
              <li>
                <i className="fas fa-check-circle" /> Load calculation &amp;
                distribution planning
              </li>
              <li>
                <i className="fas fa-check-circle" /> System upgrades &
                modernisation
              </li>
              <li>
                <i className="fas fa-check-circle" /> Smart-home integration
              </li>
              <li>
                <i className="fas fa-check-circle" /> Energy-efficiency
                optimisation
              </li>
              <li>
                <i className="fas fa-check-circle" /> Full code compliance
              </li>
            </ul>
            <Link to="/contact" className="btn">
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="service-process"
        id="service-process"
        data-aos="fade-up"
      >
        <h2 className="section-title">Our Circuit Design Process</h2>
        <p className="section-description">
          A systematic approach for optimal results.
        </p>

        <div className="process-steps">
          {processSteps.map((s, i) => (
            <div
              key={i}
              className="process-step"
              data-aos="fade-up"
              data-aos-delay={100 * (i + 1)}
            >
              <div className="step-number">{s.number}</div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="circuit-showcase" data-aos="fade-up">
        <div className="showcase-content">
          <h2 className="showcase-title">Our Design Expertise</h2>

          <div className="circuit-gallery">
            {galleryItems.map((g, i) => (
              <div className="gallery-item" key={i}>
                <img src={`${pub}/${g.src}`} alt={g.alt} />
                <div className="gallery-item-overlay">
                  <h4>{g.label}</h4>
                </div>
              </div>
            ))}
          </div>

          <Link to="/projects" className="btn btn-outline">
            View Full Portfolio
          </Link>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="service-benefits" data-aos="fade-up">
        <h2 className="section-title">Why Choose Our Circuit Design</h2>
        <p className="section-description">
          Professional solutions for optimal performance.
        </p>

        <div className="benefits-grid">
          {benefits.map((b, i) => (
            <div className="benefit-card" key={i}>
              <div className="benefit-icon">
                <i className={b.icon} />
              </div>
              <div className="benefit-content">
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ 
      <section className="faq-section" data-aos="fade-up">
        <h2 className="section-title">Common Questions</h2>

        <div className="faq-container">
          {faqs.map((f, i) => (
            <div
              key={i}
              className="faq-item"
              data-aos="fade-up"
              data-aos-delay={100 * (i + 1)}
              onClick={e => e.currentTarget.classList.toggle("active")}
            >
              <div className="faq-question">
                <span>{f.q}</span>
                <i className="fas fa-chevron-down" />
              </div>
              <div className="faq-answer">
                <p>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section> */}

      {/* CTA */}
      <section className="cta-section" data-aos="fade-up">
        <div className="cta-content">
          <h2>Ready to Power Your Project?</h2>
          <p>Contact us today for a free circuit-design consultation and quote.</p>
          <div className="cta-buttons">
            <a href="tel:+27824989477" className="btn btn-white">
              <i className="fas fa-phone" /> Call (+27) 82 498 9477
            </a>
            <Link to="/contact" className="btn btn-outline">
              <i className="fas fa-envelope" /> Email Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
